/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import React from 'react';

import { KitElement } from 'modules/teams/domain/enums/kit-element';
import { KitPattern } from 'modules/teams/domain/enums/kit-pattern';
import { theme } from 'styles/theme/theme';

import { GarmentPattern } from './garment-pattern';
import { DEFAULT_COLOR, KitState, makeDefaultElementState } from './kit-state';
import {
  KIT_VIEWBOX,
  KIT_SHIRT_PATH,
  KIT_SHIRT_TRANSFORM,
  KIT_SHORTS_PATH,
  KIT_SOCKS_PATH,
  KIT_SHIRT_PATTERN_BOX,
  KIT_SHORTS_PATTERN_BOX,
  KIT_SOCKS_PATTERN_BOX,
  KIT_STROKE,
} from './kit-wikimedia-paths';

interface KitPreviewProps {
  elementStates: Partial<KitState>;
  allowedElements: KitElement[];
}

// ── KitPreview (Wikimedia Kit_blank.svg template) ───────────────────────────

export const KitPreview: React.FC<KitPreviewProps> = ({ elementStates, allowedElements }) => {
  const shirt = elementStates[KitElement.SHIRT] ?? makeDefaultElementState();
  const shorts = elementStates[KitElement.SHORTS] ?? makeDefaultElementState();
  const socks = elementStates[KitElement.SOCKS] ?? makeDefaultElementState();

  const hasShirt = allowedElements.includes(KitElement.SHIRT);
  const hasShorts = allowedElements.includes(KitElement.SHORTS);
  const hasSocks = allowedElements.includes(KitElement.SOCKS);

  const shirtColor = shirt.colors[0] ?? DEFAULT_COLOR;
  const shortsColor = shorts.colors[0] ?? DEFAULT_COLOR;
  const socksColor = socks.colors[0] ?? DEFAULT_COLOR;

  return (
    <div
      css={css`
        width: 100%;
        display: flex;
        justify-content: center;
      `}
    >
      <svg viewBox={KIT_VIEWBOX} width="100%" style={{ maxWidth: 220 }} role="img" aria-label="Предпросмотр формы">
        <rect width="100" height="135" rx="8" fill={theme.colors.SurfaceCultured10} />

        {/* Draw order matches Kit_blank.svg: socks → shorts → shirt */}
        {hasSocks && (
          <g>
            <defs>
              <clipPath id="clip-socks">
                <path d={KIT_SOCKS_PATH} />
              </clipPath>
            </defs>
            <path d={KIT_SOCKS_PATH} fill={socksColor} {...KIT_STROKE} />
            {socks.pattern !== KitPattern.SOLID && socks.colors[1] && (
              <g clipPath="url(#clip-socks)">
                <GarmentPattern pattern={socks.pattern} color2={socks.colors[1]} box={KIT_SOCKS_PATTERN_BOX} id="pat-socks" />
              </g>
            )}
          </g>
        )}

        {hasShorts && (
          <g>
            <defs>
              <clipPath id="clip-shorts">
                <path d={KIT_SHORTS_PATH} />
              </clipPath>
            </defs>
            <path d={KIT_SHORTS_PATH} fill={shortsColor} {...KIT_STROKE} />
            {shorts.pattern !== KitPattern.SOLID && shorts.colors[1] && (
              <g clipPath="url(#clip-shorts)">
                <GarmentPattern pattern={shorts.pattern} color2={shorts.colors[1]} box={KIT_SHORTS_PATTERN_BOX} id="pat-shorts" />
              </g>
            )}
          </g>
        )}

        {hasShirt && (
          <g transform={KIT_SHIRT_TRANSFORM}>
            <defs>
              <clipPath id="clip-shirt">
                <path d={KIT_SHIRT_PATH} />
              </clipPath>
            </defs>
            <path d={KIT_SHIRT_PATH} fill={shirtColor} {...KIT_STROKE} />
            {shirt.pattern !== KitPattern.SOLID && shirt.colors[1] && (
              <g clipPath="url(#clip-shirt)">
                <GarmentPattern pattern={shirt.pattern} color2={shirt.colors[1]} box={KIT_SHIRT_PATTERN_BOX} id="pat-shirt" />
              </g>
            )}
          </g>
        )}
      </svg>
    </div>
  );
};

export default KitPreview;
